import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Badge,
} from '@mui/material';
import {
  Warning as WarningIcon,
  Error as ErrorIcon,
  CheckCircle as CheckIcon,
  Info as InfoIcon,
  NotificationsActive as NotificationIcon,
} from '@mui/icons-material';
import { parseISO, formatDistanceToNow } from 'date-fns';
import { it } from 'date-fns/locale';

// Icona per severità
const getSeverityIcon = (severity) => {
  switch (severity) {
    case 'critical':
      return <ErrorIcon sx={{ color: '#b71c1c' }} />;
    case 'alarm':
      return <ErrorIcon sx={{ color: '#f44336' }} />;
    case 'warning':
      return <WarningIcon sx={{ color: '#ff9800' }} />;
    default:
      return <InfoIcon sx={{ color: '#2196f3' }} />;
  }
};

// Colore per severità
const getSeverityColor = (severity) => {
  switch (severity) {
    case 'critical':
      return '#b71c1c';
    case 'alarm':
      return '#f44336';
    case 'warning':
      return '#ff9800';
    default:
      return '#2196f3';
  }
};

// Etichetta per tipo allarme
const getAlarmTypeLabel = (type) => {
  const labels = {
    delta_position: 'Spostamento',
    quality: 'Qualità GNSS',
    battery: 'Batteria',
    offline: 'Offline',
    velocity: 'Velocità',
  };
  return labels[type] || type;
};

// Singolo elemento allarme
function AlarmItem({ alarm, onAcknowledge, onResolve, compact = false }) {
  const timeAgo = formatDistanceToNow(parseISO(alarm.created_at), {
    addSuffix: true,
    locale: it,
  });

  if (compact) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 1,
          borderLeft: `3px solid ${getSeverityColor(alarm.severity)}`,
          bgcolor: 'background.paper',
          borderRadius: '0 4px 4px 0',
          mb: 1,
        }}
      >
        {getSeverityIcon(alarm.severity)}
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="body2" noWrap fontWeight={500}>
            {alarm.sensor_name || alarm.sensor_id?.slice(0, 8)}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {alarm.message}
          </Typography>
        </Box>
        <Typography variant="caption" color="text.secondary">
          {timeAgo}
        </Typography>
      </Box>
    );
  }

  return (
    <Card
      className={`alarm-item ${alarm.severity}`}
      sx={{
        mb: 1,
        borderLeft: `4px solid ${getSeverityColor(alarm.severity)}`,
      }}
    >
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {getSeverityIcon(alarm.severity)}
            <Box>
              <Typography variant="subtitle2" fontWeight={600}>
                {alarm.sensor_name || `Sensore ${alarm.sensor_id?.slice(0, 8)}`}
              </Typography>
              <Chip
                label={getAlarmTypeLabel(alarm.type)}
                size="small"
                sx={{ height: 20, fontSize: 11, mt: 0.5 }}
              />
            </Box>
          </Box>
          <Typography variant="caption" color="text.secondary">
            {timeAgo}
          </Typography>
        </Box>

        <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
          {alarm.message}
        </Typography>

        {alarm.value !== undefined && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            Valore: {alarm.value?.toFixed(3)} | Soglia: {alarm.threshold?.toFixed(3)}
          </Typography>
        )}

        <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
          {!alarm.acknowledged && onAcknowledge && (
            <Chip
              label="Prendi in carico"
              size="small"
              onClick={() => onAcknowledge(alarm.id)}
              sx={{ cursor: 'pointer' }}
            />
          )}
          {alarm.acknowledged && !alarm.resolved && (
            <Chip
              label={`Preso in carico da ${alarm.acknowledged_by}`}
              size="small"
              color="info"
              icon={<CheckIcon />}
            />
          )}
          {onResolve && !alarm.resolved && (
            <Chip
              label="Risolvi"
              size="small"
              color="success"
              onClick={() => onResolve(alarm.id)}
              sx={{ cursor: 'pointer' }}
            />
          )}
        </Box>
      </CardContent>
    </Card>
  );
}

// Pannello allarmi principale
function AlarmPanel({
  alarms = [],
  onAcknowledge,
  onResolve,
  title = 'Allarmi attivi',
  maxHeight = 400,
  compact = false,
}) {
  const sortedAlarms = [...alarms].sort((a, b) => {
    // Prima per severità
    const severityOrder = { critical: 0, alarm: 1, warning: 2, info: 3 };
    const sevDiff = severityOrder[a.severity] - severityOrder[b.severity];
    if (sevDiff !== 0) return sevDiff;
    // Poi per data
    return new Date(b.created_at) - new Date(a.created_at);
  });

  const criticalCount = alarms.filter((a) => a.severity === 'critical' || a.severity === 'alarm').length;
  const warningCount = alarms.filter((a) => a.severity === 'warning').length;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Badge badgeContent={alarms.length} color="error">
            <NotificationIcon color="action" />
          </Badge>
          <Typography variant="h6" fontWeight={600}>
            {title}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {criticalCount > 0 && (
            <Chip
              icon={<ErrorIcon />}
              label={criticalCount}
              size="small"
              color="error"
            />
          )}
          {warningCount > 0 && (
            <Chip
              icon={<WarningIcon />}
              label={warningCount}
              size="small"
              color="warning"
            />
          )}
        </Box>
      </Box>

      <Box
        sx={{
          maxHeight,
          overflowY: 'auto',
          pr: 1,
        }}
      >
        {sortedAlarms.length === 0 ? (
          <Box
            sx={{
              p: 3,
              textAlign: 'center',
              bgcolor: 'success.dark',
              opacity: 0.8,
              borderRadius: 2,
            }}
          >
            <CheckIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
            <Typography color="success.main" fontWeight={500}>
              Nessun allarme attivo
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Tutti i sensori funzionano correttamente
            </Typography>
          </Box>
        ) : (
          sortedAlarms.map((alarm) => (
            <AlarmItem
              key={alarm.id}
              alarm={alarm}
              onAcknowledge={onAcknowledge}
              onResolve={onResolve}
              compact={compact}
            />
          ))
        )}
      </Box>
    </Box>
  );
}

export default AlarmPanel;
export { AlarmItem };
